import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

/**
 * The window every Paper Pricing pop-up opens in (ledger row 132).
 *
 * Rendered into document.body: a pop-up drawn inside the page sits under
 * anything in a sticky or scrolling layer above it, and raising its z-index
 * never fixes that (the delivery pane incident, 2026-09-14).
 *
 * With `onSubmit` the window is itself the form, so Enter saves and the footer
 * buttons belong to it. It does not close on a click outside: several of these
 * hold typed work that a stray click would throw away.
 */
const PaperPricingModal = ({
    title,
    eyebrow,
    onClose,
    busy = false,
    wide = false,
    onSubmit,
    footer,
    children,
}) => {
    useEffect(() => {
        const onKey = (event) => {
            if (event.key === 'Escape' && !busy) onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose, busy]);

    // The dialog is the outer box; the form (when there is one) sits inside it,
    // because a <form> may not itself take the dialog role.
    const Frame = onSubmit ? 'form' : 'div';
    const frameProps = onSubmit ? { onSubmit, noValidate: true } : {};

    return ReactDOM.createPortal(
        <div className="pp-overlay">
            <div
                className={`pp-modal ${wide ? 'pp-modal--wide' : ''}`}
                role="dialog"
                aria-modal="true"
                aria-label={title}
            >
                <Frame {...frameProps} className="pp-modal-frame">
                    <div className="pp-modal-head">
                        <div className="pp-modal-titles">
                            {eyebrow ? <div className="pp-modal-eyebrow">{eyebrow}</div> : null}
                            <h2 className="pp-modal-title">{title}</h2>
                        </div>
                        <button
                            type="button"
                            className="pp-icon-btn"
                            onClick={onClose}
                            disabled={busy}
                            aria-label="Close"
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    </div>
                    <div className="pp-modal-body">{children}</div>
                    {footer ? <div className="pp-modal-foot">{footer}</div> : null}
                </Frame>
            </div>
        </div>,
        document.body,
    );
};

export default PaperPricingModal;
